import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Zap, Globe, Video, FileText, CheckCircle, Lightbulb } from 'lucide-react';

const SolutionIntroSection = () => {
  const { isMobile } = useBreakpoint();

  const components = [
    {
      icon: Globe,
      title: "Professional Landing Page",
      description: "Fully designed, conversion-optimized page ready to sell your Genius Card service"
    },
    {
      icon: Video,
      title: "White Label Video Sales Letter",
      description: "Professional video that sells for you - just add your branding and go live"
    },
    {
      icon: FileText,
      title: "6 Industry-Specific Templates",
      description: "Pre-written business card templates for the most profitable niches"
    }
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-[#003a70] via-[#003a70]/95 to-[#003a70]/90" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
      backgroundPattern="dots"
      patternColor="#FEE6D8"
      patternOpacity={3}
    >
      
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/90 rounded-full shadow-lg border-2 border-[#FEE6D8]/50">
            <Lightbulb className="text-[#003a70]" size={isMobile ? 24 : 28} />
            <span className="text-[#003a70] font-bold text-lg md:text-xl">The Solution</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-white leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            What If You Could Skip All That 
            <br />
            <span className="text-[#FEE6D8]">and Start Selling Tomorrow?</span>
          </h2>
          
          <p className={cn(
            "text-white/90 leading-relaxed max-w-3xl mx-auto",
            isMobile ? "text-lg" : "text-xl lg:text-2xl"
          )}>
            Instead of spending months creating everything from scratch, what if someone just handed you the complete system?
          </p>
        </div>

        {/* Solution Reveal */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-8 md:p-12 border-2 border-[#FEE6D8]/30 shadow-2xl max-w-6xl mx-auto overflow-hidden">
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FEE6D8]/10 via-transparent to-[#FEE6D8]/5 pointer-events-none"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <Zap className="text-white" size={32} />
                </div>
              </div>
              
              <h3 className={cn(
                "font-bold text-[#003a70] mb-8 leading-tight",
                isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
              )}>
                Introducing the Complete
                <br />
                <span className="text-[#FEE6D8] bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
                  Genius Card Playbook DFY Bundle
                </span>
              </h3>
              
              <p className={cn(
                "text-[#003a70]/80 leading-relaxed mb-10 max-w-4xl mx-auto",
                isMobile ? "text-base" : "text-lg lg:text-xl"
              )}>
                Everything you need to start selling high-converting business cards immediately - 
                professionally designed, written, and ready to deploy.
              </p>
              
              {/* Components Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-10">
                {components.map((component, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-[#FEE6D8]/30 via-white to-[#FEE6D8]/10 rounded-2xl p-6 border border-[#FEE6D8]/40 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                        <component.icon className="text-white" size={28} />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#003a70] mb-3 text-lg">
                          {component.title}
                        </h4>
                        <p className="text-[#003a70]/70 text-sm leading-relaxed">
                          {component.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Value Proposition */}
              <div className="bg-gradient-to-br from-[#003a70]/5 via-[#FEE6D8]/10 to-[#003a70]/5 rounded-3xl p-8 border-2 border-[#FEE6D8]/30 shadow-inner">
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full flex items-center justify-center shadow-lg">
                    <CheckCircle className="text-[#003a70]" size={24} />
                  </div>
                </div>
                
                <h4 className={cn(
                  "font-bold text-[#003a70] mb-6 leading-tight",
                  isMobile ? "text-xl" : "text-2xl lg:text-3xl"
                )}>
                  Skip 7-10 Weeks of Work
                </h4>
                
                <div className="space-y-4">
                  <p className={cn(
                    "text-[#003a70]/90 leading-relaxed",
                    isMobile ? "text-base" : "text-lg"
                  )}>
                    Instead of spending months creating landing pages, writing copy, and designing templates...
                  </p>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-[#FEE6D8]/30 shadow-inner">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                      <div className="space-y-3">
                        <h5 className="font-semibold text-[#003a70] mb-3 flex items-center gap-2">
                          <CheckCircle className="text-green-600" size={18} />
                          You Get Instantly:
                        </h5>
                        <ul className="space-y-2 text-[#003a70]/80">
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 bg-[#FEE6D8] rounded-full mt-2 flex-shrink-0"></div>
                            <span>Professional sales page that converts</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 bg-[#FEE6D8] rounded-full mt-2 flex-shrink-0"></div>
                            <span>Video that sells for you 24/7</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 bg-[#FEE6D8] rounded-full mt-2 flex-shrink-0"></div>
                            <span>Templates for profitable niches</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-3">
                        <h5 className="font-semibold text-[#003a70] mb-3 flex items-center gap-2">
                          <Zap className="text-[#FEE6D8]" size={18} />
                          Result:
                        </h5>
                        <div className="bg-gradient-to-r from-[#FEE6D8]/20 to-[#FEE6D8]/10 rounded-lg p-4 border-l-4 border-[#FEE6D8]">
                          <p className="font-bold text-[#003a70] text-lg">
                            Start selling tomorrow, not next quarter
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default SolutionIntroSection;
